/**
 * @fileoverview Prompt template for plot-extractor-v2.
 *
 * Per-storyType guidance is selected at render time by the
 * `[[[writtenPlotGuidance]]]` branching macro (see
 * `src/ai/agents/branching-macros.ts`). Falls back to Novel-level guidance
 * when the storyType is missing or unrecognised — matching the legacy
 * `prompts['Novel']` default.
 */

export const prompt = `
You are an expert story analyst specializing in long-form fiction.

## TASK

Read the following document and extract two outputs:

1. **summary**: A concise overview of the plot (100–150 words) that weaves together the story summary, its central theme, and the main conflict.
2. **writtenPlot**: A detailed, multi-paragraph plot outline in Markdown format that serves as the primary reference for subsequent story development.

The document may be a complete story, a summary, or a collection of notes. Your job is to find the narrative through-line and distill it into these two outputs.

NOTE: Character extraction is handled separately by a dedicated agent. Focus only on plot structure.

{{#if scopeGuidance}}
## SCOPE GUIDANCE

{{{scopeGuidance}}}
{{/if}}

## DOCUMENT

{{{textInput}}}

---

## OUTPUT INSTRUCTIONS

Provide your output in the required JSON format.

### summary

Write a concise 100–150 word overview that weaves together:
- What happens in the story (plot summary)
- The central theme or message
- The main conflict driving the narrative

### writtenPlot

Write a detailed plot outline in Markdown format, organized under the 7 structural phase headings:

## Initial State
[Prose covering the status quo before disruption]

## Inciting Incident
[Prose covering the catalyst event that kicks the story into motion]

## Break Into Two
[Prose covering the protagonist's commitment to the journey]

## Midpoint
[Prose covering the major shift — revelation, raised stakes, protagonist becomes proactive]

## Break Into Three
[Prose covering the lowest point — all seems lost]

## Climax
[Prose covering the defining confrontation and resolution]

## Final State
[Prose covering the new equilibrium and transformation]

[[[writtenPlotGuidance]]]

Write in present tense, third person. Describe plot events as concrete actions ("Elena discovers the hidden letter" not "something is revealed"). Each phase section should be 1-3 paragraphs. Use **bold** for character names and key plot developments. Do NOT use HTML tags.
`;
